import { Controller } from "@hotwired/stimulus"

const STEPS = [
  {
    id: "intent",
    message: "What do you need help with?",
    field: "intent",
    options: [
      { label: "Roof repair (storm/leak/damage)", value: "repair", service: "roof_repair" },
      { label: "Roof replacement", value: "replacement", service: "roof_replacement" },
      { label: "Insurance claim help", value: "insurance_claim", service: "insurance_claim_help" },
      { label: "Inspection / maintenance", value: "inspection", service: "inspection" }
    ]
  },
  {
    id: "urgency",
    message: "How soon do you need this done?",
    field: "urgency",
    options: [
      { label: "ASAP (emergency)", value: "asap" },
      { label: "Within 2 weeks", value: "two_weeks" },
      { label: "Within a month", value: "one_month" },
      { label: "Just planning ahead", value: "planning" }
    ]
  },
  {
    id: "roof_type",
    message: "What type of roofing do you have?",
    field: "roof_type",
    options: [
      { label: "Asphalt shingles", value: "asphalt_shingles" },
      { label: "Metal", value: "metal" },
      { label: "Tile", value: "tile" },
      { label: "Flat", value: "flat" },
      { label: "Not sure", value: "not_sure" }
    ]
  },
  {
    id: "roof_size",
    message: "How large is your roof?",
    field: "roof_size",
    skippable: true,
    options: [
      { label: "Small (under 1,500 sq ft)", value: "small" },
      { label: "Medium (1,500 - 2,500 sq ft)", value: "medium" },
      { label: "Large (over 2,500 sq ft)", value: "large" },
      { label: "Not sure", value: "not_sure" }
    ]
  },
  {
    id: "insurance",
    message: "Will you be filing an insurance claim?",
    field: "insurance",
    conditional: (answers) => answers.intent === "insurance_claim",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" }
    ]
  }
]

const STORAGE_KEY = "rooferrate_guided_search"

export default class extends Controller {
  static targets = ["modal", "stepContent", "progressBar", "progressText", "backButton"]
  static values = { quotesUrl: String }

  connect() {
    this.answers = {}
    this.currentStepIndex = 0
  }

  open(event) {
    event?.preventDefault()

    this.restore()
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.setAttribute("aria-modal", "true")
    document.body.style.overflow = "hidden"
    this.renderStep()
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.removeAttribute("aria-modal")
    document.body.style.overflow = ""
  }

  back() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--
      // Skip conditional steps going backward
      while (this.currentStepIndex > 0 && this.shouldSkip(STEPS[this.currentStepIndex])) {
        this.currentStepIndex--
      }
      this.renderStep()
    }
  }

  selectOption(event) {
    const step = STEPS[this.currentStepIndex]
    const value = event.currentTarget.dataset.value
    const service = event.currentTarget.dataset.service

    this.answers[step.field] = value
    if (service) this.answers._service = service

    this.save()
    this.advance()
  }

  advance() {
    this.currentStepIndex++

    // Skip conditional steps that don't apply
    while (this.currentStepIndex < STEPS.length && this.shouldSkip(STEPS[this.currentStepIndex])) {
      this.currentStepIndex++
    }

    if (this.currentStepIndex >= STEPS.length) {
      this.submit()
    } else {
      this.renderStep()
    }
  }

  shouldSkip(step) {
    return step.conditional && !step.conditional(this.answers)
  }

  renderStep() {
    const step = STEPS[this.currentStepIndex]
    const totalVisible = STEPS.filter(s => !this.shouldSkip(s)).length
    const currentVisible = STEPS.slice(0, this.currentStepIndex + 1).filter(s => !this.shouldSkip(s)).length
    const progress = Math.round((currentVisible / totalVisible) * 100)

    this.progressBarTarget.style.width = `${progress}%`
    this.progressTextTarget.textContent = `Step ${currentVisible} of ${totalVisible}`
    this.backButtonTarget.classList.toggle("invisible", this.currentStepIndex === 0)

    let html = `<h2 class="text-xl font-bold text-slate-900 mb-6">${step.message}</h2>`
    html += `<div class="flex flex-col gap-3">`

    step.options.forEach(option => {
      html += `
        <button type="button"
          class="w-full text-left px-5 py-4 bg-white text-slate-900 border border-slate-200 rounded-xl hover:bg-emerald-50 hover:border-emerald-300 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 text-base"
          data-action="click->guided-search#selectOption"
          data-value="${option.value}"
          ${option.service ? `data-service="${option.service}"` : ""}>
          ${option.label}
        </button>`
    })

    if (step.skippable) {
      html += `
        <button type="button"
          class="text-sm text-slate-400 hover:text-slate-600 mt-2 cursor-pointer"
          data-action="click->guided-search#selectOption"
          data-value="not_sure">
          Skip this step
        </button>`
    }

    html += `</div>`
    this.stepContentTarget.innerHTML = html
  }

  submit() {
    // Need ZIP before we can go to results
    if (!this.answers.zip) {
      this.showZipInput()
      return
    }

    this.goToResults()
  }

  showZipInput() {
    this.stepContentTarget.innerHTML = `
      <h2 class="text-xl font-bold text-slate-900 mb-6">Where's your property located?</h2>
      <form data-action="submit->guided-search#submitZip" class="flex flex-col gap-3">
        <input type="text" name="zip" placeholder="Enter your ZIP code" required pattern="[0-9]{5}" inputmode="numeric"
          class="px-5 py-4 border border-slate-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        <button type="submit"
          class="px-5 py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors cursor-pointer text-base">
          Find My Matches
        </button>
      </form>
    `
  }

  submitZip(event) {
    event.preventDefault()
    const zip = event.currentTarget.querySelector("input[name='zip']").value.trim()
    if (zip) {
      this.answers.zip = zip
      this.save()
      this.goToResults()
    }
  }

  goToResults() {
    const params = new URLSearchParams()
    params.set("zip", this.answers.zip)
    params.set("service", this.answers._service || "roof_repair")
    params.set("intent", this.answers.intent || "repair")
    if (this.answers.urgency) params.set("urgency", this.answers.urgency)
    if (this.answers.roof_type) params.set("roof_type", this.answers.roof_type)
    if (this.answers.roof_size) params.set("roof_size", this.answers.roof_size)
    if (this.answers.insurance) params.set("insurance", this.answers.insurance)

    this.clear()
    window.location.href = `${this.quotesUrlValue}?${params.toString()}`
  }

  // Email capture (called from results page CTA)
  openEmailCapture(event) {
    const recipientIds = event.currentTarget.dataset.recipientIds
    if (!recipientIds) return

    // Build and show a simple email modal
    const overlay = document.createElement("div")
    overlay.id = "email-capture-overlay"
    overlay.className = "fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    overlay.innerHTML = `
      <div class="bg-white rounded-2xl max-w-md w-full p-6" role="dialog" aria-modal="true" aria-label="Get quotes">
        <h2 class="text-xl font-bold text-slate-900 mb-2">Get your free quotes</h2>
        <p class="text-slate-600 text-sm mb-4">Enter your contact info and we'll send your request to the selected pros.</p>
        <form id="inquiry-form">
          <input type="hidden" name="recipient_ids" value="${recipientIds}" />
          <div class="flex flex-col gap-3">
            <input type="email" name="email" placeholder="Email address" required
              class="px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            <input type="text" name="name" placeholder="Your name (optional)"
              class="px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            <input type="tel" name="phone" placeholder="Phone (optional)"
              class="px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            <div id="inquiry-turnstile-container"></div>
            <button type="submit"
              class="px-4 py-3 min-h-[48px] bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors cursor-pointer">
              Send My Request
            </button>
            <button type="button" onclick="document.getElementById('email-capture-overlay').remove()"
              class="text-sm text-slate-400 hover:text-slate-600 cursor-pointer">
              Cancel
            </button>
          </div>
        </form>
      </div>
    `
    document.body.appendChild(overlay)

    // Render turnstile
    const container = overlay.querySelector("#inquiry-turnstile-container")
    let turnstileToken = ""
    if (window.turnstile) {
      window.turnstile.render(container, {
        sitekey: document.querySelector("meta[name='turnstile-site-key']")?.content || "1x00000000000000000000AA",
        theme: "light",
        size: "compact",
        callback: (token) => { turnstileToken = token }
      })
    }

    // Handle form submit
    const form = overlay.querySelector("#inquiry-form")
    form.addEventListener("submit", async (e) => {
      e.preventDefault()
      const submitBtn = form.querySelector("button[type='submit']")
      submitBtn.disabled = true
      submitBtn.textContent = "Sending..."

      const params = new URLSearchParams(window.location.search)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      try {
        const response = await fetch("/inquiries", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({
            inquiry: {
              email: form.querySelector("[name='email']").value,
              name: form.querySelector("[name='name']").value,
              phone: form.querySelector("[name='phone']").value,
              intent: params.get("intent") || "repair",
              urgency: params.get("urgency"),
              roof_type: params.get("roof_type"),
              roof_size: params.get("roof_size"),
              zip: params.get("zip"),
              insurance_help: params.get("insurance") === "yes",
              source_page: window.location.pathname
            },
            recipient_ids: recipientIds.split(","),
            turnstile_token: turnstileToken
          })
        })

        const data = await response.json()
        if (response.ok) {
          window.location.href = data.redirect_url
        } else {
          alert(data.errors?.join(", ") || "Something went wrong.")
          submitBtn.disabled = false
          submitBtn.textContent = "Send My Request"
        }
      } catch {
        alert("Connection error. Please try again.")
        submitBtn.disabled = false
        submitBtn.textContent = "Send My Request"
      }
    })
  }

  save() {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      answers: this.answers,
      step: this.currentStepIndex
    }))
  }

  restore() {
    const saved = sessionStorage.getItem(STORAGE_KEY)
    if (saved) {
      const data = JSON.parse(saved)
      this.answers = data.answers || {}
      this.currentStepIndex = data.step || 0
    }
  }

  clear() {
    sessionStorage.removeItem(STORAGE_KEY)
  }
}
